import {
  NetworkExportErrors,
  NetworkExportErrorTypes,
} from './getReleaseErrorsForAVariable'

export type ReleaseErrorGroup = {
  type: NetworkExportErrorTypes
  label: string
  count: number
  subjectNames: string[]
}

export const MAX_LISTED_SUBJECTS = 6

// First human-readable rendering of release errors — until now they only ever
// reached a researcher as the raw typed JSON download (KBE-752 left the gap
// deliberately). `Record` over the enum, not a partial map with a fallback, so
// adding an error type without a label is a type error rather than a row
// reading "UNKNOWN_HASHTAG".
export const RELEASE_ERROR_LABELS: Record<NetworkExportErrorTypes, string> = {
  [NetworkExportErrorTypes.UNKNOWN_HASHTAG]: 'Unrecognized tag',
  [NetworkExportErrorTypes.INVALID_HASHTAG]: 'Malformed tag value',
  [NetworkExportErrorTypes.MISSING_HASHTAG]: 'Missing required tag',
  [NetworkExportErrorTypes.DUPLICATE_HASHTAG]: 'Repeated single-value tag',
  [NetworkExportErrorTypes.INCONSISTENT_FAULTS]: 'Fault on a missing state',
  [NetworkExportErrorTypes.NON_PUBLISHABLE_PUBLIC_VARIABLE]:
    'Public node of a non-publishable type',
  [NetworkExportErrorTypes.MISSING_DIAGNOSIS_NODE]:
    'Disease without a Diagnosis node',
  [NetworkExportErrorTypes.MULTIPLE_DIAGNOSIS_NODES]:
    'Disease with more than one Diagnosis node',
  [NetworkExportErrorTypes.INVALID_DIAGNOSIS_NAME]:
    'Diagnosis name does not match its Disease',
  [NetworkExportErrorTypes.DISEASE_DIAGNOSIS_PUBLIC_MISMATCH]:
    'Disease and Diagnosis disagree on being public',
  [NetworkExportErrorTypes.BROKEN_LINK]: 'Broken reference link',
  [NetworkExportErrorTypes.INVALID_STATE_NAMES]: 'Invalid state names',
  [NetworkExportErrorTypes.NON_DISCRETE_PUBLIC_VARIABLE]:
    'Public node with intervals that must be discrete',
  [NetworkExportErrorTypes.MISSING_OPTIMAL_STATE]: 'Missing optimal state',
  [NetworkExportErrorTypes.NON_SEMICONTINUOUS_TRACKED_SYMPTOM]:
    'Tracked Symptom without intervals',
}

// A missing- or duplicate-tag row is only actionable if it says *which* tag, and
// one release attempt can hit several different tags — so the tag name joins the
// label rather than collapsing every such error into one row.
function getTagName(error: NetworkExportErrors): string | null {
  switch (error.type) {
    case NetworkExportErrorTypes.MISSING_HASHTAG:
      return error.missingTag
    case NetworkExportErrorTypes.DUPLICATE_HASHTAG:
      return error.duplicateTag
    default:
      return null
  }
}

function getGroupKey(error: NetworkExportErrors): string {
  const tagName = getTagName(error)
  return tagName === null ? error.type : `${error.type}:${tagName}`
}

function getGroupLabel(error: NetworkExportErrors): string {
  const label = RELEASE_ERROR_LABELS[error.type]
  const tagName = getTagName(error)
  return tagName === null ? label : `${label}: ${tagName}`
}

// Which entity the researcher has to go and fix. Most errors name a variable;
// the Disease–Diagnosis checks are about a disease and its diagnosis instead.
function getSubjectName(error: NetworkExportErrors): string {
  switch (error.type) {
    case NetworkExportErrorTypes.MISSING_DIAGNOSIS_NODE:
      return error.diseaseName
    case NetworkExportErrorTypes.MULTIPLE_DIAGNOSIS_NODES:
      return `${error.diseaseName} (${error.diagnosisNames.join(', ')})`
    case NetworkExportErrorTypes.INVALID_DIAGNOSIS_NAME:
    case NetworkExportErrorTypes.DISEASE_DIAGNOSIS_PUBLIC_MISMATCH:
      return `${error.diseaseName} / ${error.diagnosisName}`
    default:
      return error.variableName
  }
}

export function groupReleaseErrors(
  errors: NetworkExportErrors[],
): ReleaseErrorGroup[] {
  const groups = new Map<string, ReleaseErrorGroup>()

  errors.forEach((error) => {
    const key = getGroupKey(error)
    const subjectName = getSubjectName(error)
    const group = groups.get(key)
    if (group) {
      group.count += 1
      if (!group.subjectNames.includes(subjectName)) {
        group.subjectNames.push(subjectName)
      }
      return
    }
    groups.set(key, {
      type: error.type,
      label: getGroupLabel(error),
      count: 1,
      subjectNames: [subjectName],
    })
  })

  return [...groups.values()].sort((a, b) => b.count - a.count)
}

export function formatSubjectNames(
  subjectNames: string[],
  maxListed: number = MAX_LISTED_SUBJECTS,
): string {
  const listed = subjectNames.slice(0, maxListed).join(' · ')
  const hidden = subjectNames.length - maxListed
  return hidden > 0 ? `${listed} · +${hidden} more` : listed
}
